import { useState, useEffect } from 'react';
import { getUIOptionsRequest } from '@/api/options';
import { FilterConfig } from '@/types/ui';

interface Option {
  value: string;
  label: string;
}

interface CountryOption extends Option {
  provinces?: Option[];
  states?: Option[];
}

interface PropertyPageOptionsData {
  filters: FilterConfig[];
  fields: {
    country: CountryOption[];
  };
}

// Assuming UIOptionsResponse is the type returned by getUIOptionsRequest
// and it might not strictly conform to PropertyPageOptionsData initially.
// We'll define a more flexible type for the raw response data if needed,
// or cast it if we're certain of its structure at runtime.
interface UIOptionsResponseData {
  filters?: FilterConfig[];
  fields?: {
    country?: CountryOption[];
    // Add other fields if they exist in the actual API response
  };
  // Add other properties that might exist in the raw response
}


interface UsePropertyPageOptionsReturn {
  filtersConfig: FilterConfig[];
  optionsData: PropertyPageOptionsData | null; // Added to return the full data object
  isLoading: boolean;
  error?: string;
}

const usePropertyPageOptions = (path: string): UsePropertyPageOptionsReturn => {
  const [filtersConfig, setFiltersConfig] = useState<FilterConfig[]>([]);
  const [optionsData, setOptionsData] = useState<PropertyPageOptionsData | null>(null); // New state for full data
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    const fetchOptions = async () => {
      setIsLoading(true);
      setError(undefined);
      try {
        const response = await getUIOptionsRequest(path);
        if (response.data && response.data.data) {
          const rawData = response.data.data as UIOptionsResponseData;

          if (rawData.fields?.country) {
            setOptionsData(rawData as PropertyPageOptionsData);
          } else {
            setOptionsData({ filters: rawData.filters || [], fields: { country: [] } });
          }
          
          if (rawData.filters) {
            setFiltersConfig(rawData.filters);
          } else {
            setFiltersConfig([]);
          }
        } else {
          setOptionsData(null);
          setFiltersConfig([]);
        }
      } catch (err: any) {
        const errorMessage = err?.response?.data?.message || err?.message || `Failed to fetch UI options for ${path}`;
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    if (path) {
      fetchOptions();
    } else {
      setIsLoading(false);
    }
  }, [path]);

  return { filtersConfig, optionsData, isLoading, error };
};

export default usePropertyPageOptions;
